import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Breadcrumbs, 
  Link,
  styled
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { fetchProfile, fetchProfileImages, fetchProfileVideos } from '../utils/api';
import Gallery from '../components/Gallery';
import VideoGallery from '../components/VideoGallery';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

const INK = {
  primaria: 'rgba(255, 255, 255, 0.92)',
  secundaria: 'rgba(255, 255, 255, 0.56)',
  borde: 'rgba(255, 255, 255, 0.10)',
  hover: 'rgba(255, 255, 255, 0.09)',
  media: 'rgba(255, 255, 255, 0.06)'
};

// La cabecera era una banda de 240px a 4,8:1 con la portada de fondo. Sobre una
// foto cuadrada eso mostraba el 21% del alto: de Wendyta se veian los anteojos
// y nada mas. Y la portada ya aparece entera en la grilla de abajo, asi que la
// banda gastaba media pantalla en mutilar algo que igual se ve. Ahora la
// portada se muestra en su proporcion, chica, al lado del nombre; las 92 fotos
// son la media que manda.
const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  marginBottom: theme.spacing(5),

  '& .profile-cover': {
    flexShrink: 0,
    width: '88px',
    height: '88px',
    borderRadius: '8px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: INK.media
  },

  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    '& .profile-cover': { width: '64px', height: '64px' }
  }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  '& .loading-text': {
    marginTop: '16px',
    color: theme.palette.text.secondary,
  }
}));

const ProfileView = () => {
  const { profileId } = useParams();
  const location = useLocation();
  // La grilla ya tiene nombre y portada de la coleccion, asi que los manda al
  // navegar. Con eso la cabecera se dibuja en el primer render en vez de
  // esperar tres peticiones en serie.
  const perfilPrevio =
    location.state?.perfilPrevio?.id === profileId ? location.state.perfilPrevio : null;
  const [profile, setProfile] = useState(perfilPrevio);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 para imágenes, 1 para videos
  const { user } = useAuth();
  const navigate = useNavigate();

  // Función para cargar el perfil, sus imágenes y videos
  const fetchProfileAndImages = useCallback(async () => {
    if (!profileId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Cargar datos del perfil
      const profileResponse = await fetchProfile(profileId);
      if (!profileResponse.data) {
        throw new Error('Error loading profile');
      }
      
      setProfile(profileResponse.data);
      
      // Cargar imágenes del perfil
      const imagesResponse = await fetchProfileImages(profileId);
      setImages(imagesResponse);
      
      // Cargar videos del perfil
      try {
        const videosResponse = await fetchProfileVideos(profileId);
        setVideos(videosResponse);
      } catch (videoError) {
        console.error('Error fetching profile videos:', videoError);
        // No fallamos toda la carga si solo fallan los videos
        setVideos([]);
      }
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchProfileAndImages();
  }, [profileId, fetchProfileAndImages]);

  // Iba a `/`, que redirige a `/login`, que a su vez redirige a `/gallery`:
  // tres saltos de ruta para volver a la pantalla de al lado.
  const handleBackClick = () => {
    navigate('/gallery');
  };

  const handleUploadClick = () => {
    navigate(`/upload?profile=${profileId}`);
  };

  const handleUploadVideoClick = () => {
    if (profileId) {
      navigate(`/upload-video?profile=${profileId}`);
    } else {
      navigate('/upload-video');
    }
  };

  // Función para manejar el cambio de pestañas
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // El spinner de pantalla completa solo cuando no hay absolutamente nada que
  // mostrar. Si venimos de la grilla ya tenemos la cabecera: taparla con un
  // spinner seria esconder algo que ya esta listo.
  if (loading && !profile) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
        <LoadingContainer>
          <CircularProgress size={50} />
          <Typography className="loading-text" variant="h6">
            Loading profile...
          </Typography>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          Back to Profiles
        </Button>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Profile not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          Back to Profiles
        </Button>
      </Container>
    );
  }

  return (
    // Sin el Fade de 800ms que envolvia la pagina: era mas largo que cualquier
    // movimiento de interfaz y retrasaba ver el contenido.
    <React.Fragment>
      <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
        {/* Es navegacion secundaria: iba en 16px, mas grande que las pestañas
            que si son la eleccion principal de la pantalla. */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3, fontSize: '0.8125rem' }}>
          <Link
            color="inherit"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.8125rem',
              color: INK.secundaria,
              textDecoration: 'none',
              '&:hover': { color: INK.primaria }
            }}
            onClick={handleBackClick}
          >
            <ArrowBackIcon sx={{ mr: 0.5, fontSize: 14 }} />
            Profiles
          </Link>
          <Typography sx={{ fontSize: '0.8125rem', color: INK.primaria }}>
            {profile.name}
          </Typography>
        </Breadcrumbs>
        
        <ProfileHeader>
          <Box
            className="profile-cover"
            sx={{
              backgroundImage: `url(${profile.cover_image || `${process.env.PUBLIC_URL}/images/profile-placeholder.jpg`})`
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            {/* Sin text-shadow: el nombre ya no va encima de una foto. */}
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 500,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                color: INK.primaria
              }}
            >
              {profile.name}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: INK.secundaria }}>
              {images.length} {images.length === 1 ? 'image' : 'images'}
              {videos.length > 0 &&
                ` · ${videos.length} ${videos.length === 1 ? 'video' : 'videos'}`}
            </Typography>
            {profile.description && (
              <Typography
                sx={{ mt: 1, maxWidth: '60ch', fontSize: '0.9375rem', color: INK.secundaria }}
              >
                {profile.description}
              </Typography>
            )}
          </Box>
        </ProfileHeader>
        
        {!loading && !error && (
          <>
            {/* Las pestañas y la accion de subir viven en la misma fila: eran
                dos botones rellenos —uno morado y otro cian, dos acentos donde
                corresponde uno— repitiendo lo que las pestañas ya dicen. Ahora
                hay una sola accion y sube lo que la pestaña abierta muestra. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: `1px solid ${INK.borde}`,
                mb: 4
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="media tabs"
                sx={{
                  minHeight: 'auto',
                  '& .MuiTabs-indicator': { backgroundColor: INK.primaria, height: '2px' },
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    minWidth: 0,
                    padding: '0 0 12px',
                    marginRight: 3,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    color: INK.secundaria,
                    '&.Mui-selected': { color: INK.primaria }
                  }
                }}
              >
                <Tab label="Images" id="media-tab-0" aria-controls="media-tabpanel-0" />
                <Tab label="Videos" id="media-tab-1" aria-controls="media-tabpanel-1" />
              </Tabs>

              {user && (
                <Button
                  variant="text"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={activeTab === 0 ? handleUploadClick : handleUploadVideoClick}
                  sx={{
                    flexShrink: 0,
                    mb: 1,
                    px: { xs: 0.75, sm: 1.5 },
                    py: 0.75,
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    // En pantalla angosta pierde la caja: las pestañas son la
                    // eleccion principal y no pueden pesar menos que esto.
                    color: { xs: INK.secundaria, sm: INK.primaria },
                    border: { xs: '1px solid transparent', sm: `1px solid ${INK.borde}` },
                    '&:hover': {
                      backgroundColor: INK.hover,
                      borderColor: { sm: 'rgba(255,255,255,0.18)' }
                    }
                  }}
                >
                  Add
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                    {activeTab === 0 ? 'images' : 'videos'}
                  </Box>
                </Button>
              )}
            </Box>

            <Box sx={{ width: '100%' }}>
              
              {/* Panel de imágenes */}
              <Box
                role="tabpanel"
                hidden={activeTab !== 0}
                id="media-tabpanel-0"
                aria-labelledby="media-tab-0"
              >
                {activeTab === 0 && (
                  <Gallery 
                    images={images} 
                    loading={loading} 
                    error={error} 
                    profileId={profileId} 
                  />
                )}
              </Box>
              
              {/* Panel de videos */}
              <Box
                role="tabpanel"
                hidden={activeTab !== 1}
                id="media-tabpanel-1"
                aria-labelledby="media-tab-1"
              >
                {activeTab === 1 && (
                  <VideoGallery 
                    videos={videos} 
                    loading={false} 
                    error={null} 
                    profileId={profileId} 
                  />
                )}
              </Box>
            </Box>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default ProfileView;
